import React, { useContext, useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import { Modal } from 'Components/Modal'
import LinearProgressBar from 'Components/LinearProgressBar'

import SubmitButton from '../../../../../../Components/Form/SubmitButton/index'
import {
  archiveLocation,
  createLocation,
  updateLocationById,
} from '../../../../../../Reducers/Location/actions'
import styles from './styles'
import {
  convertScheduleResponseToDailySchedule,
  deepEqual,
  formatAddress,
  generateBackButtonLabel,
  generateNextButtonLabel,
  getDefaultCenter,
  getDefaultHoursSchedule,
  getDefaultTimeSlotsSchedule,
  prepareDailySchedulePayload,
  to12Hour,
  to24Hour,
  validateDaylySchedule,
  validateEventSchedule,
  validateWeeklySchedule,
  validateYearlySchedule,
} from './utils'

import { SnackBarContext } from 'Components/SnackBar'
import BaseInformation from './steps/BaseInformation'
import { MODAL_STEPS } from './options'
import { UnsavedChangesWarn } from './parts/UnsavedChangesWarn'
import StepsIndicator from './parts/StepsIndicator'
import WeeklyScheduling from './steps/WeeklyScheduling'
import YearlyScheduling from './steps/YearlyScheduling'
import EventSchedule from './steps/EventSchedule'
import { LOCATION_SCHEDULE_TYPES_ENUM } from '../../../../../../shared/constants'

const initialForm = {
  name: '',
  label: '',
  description: '',
  needsPermit: false,
  typeId: null,
  isEvent: false,
  isSheltered: false,
  eventType: null,
  address: '',
  location: {
    lat: 0,
    lng: 0,
  },
}

const H2HLocationModal = props => {
  const [closeWarnShown, setCloseWarnShown] = useState(false)
  const [currentStep, setCurrentStep] = useState(MODAL_STEPS.BASE_INFORMATION)
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const [mapCenter, setMapCenter] = useState({
    lat: 0,
    lng: 0,
  })

  const [baseLocationForm, setBaseLocationForm] = useState(initialForm)
  const [eventSchedule, setEventSchedule] = useState({
    startDate: null,
    endDate: null,
    breakTimes: [{ id: 1, start: null, end: null }],
  })
  const [weeklyScheduleType, setWeeklyScheduleType] = useState(
    LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS,
  )
  const [timeSlotsSchedule, setTimeSlotsSchedule] = useState(
    getDefaultTimeSlotsSchedule(),
  )
  const [weeklyHoursSchedule, setWeeklyHoursSchedule] = useState(
    getDefaultHoursSchedule(),
  )
  const [dailyHoursSchedule, setDailyHoursSchedule] = useState(
    getDefaultHoursSchedule(),
  )
  const [yearlySchedule, setYearlySchedule] = useState([
    { id: 1, start: null, end: null },
  ])
  const [includeYearlySchedule, setIncludeYearlySchedule] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    handleCancel,
    locationTypeOptions: locationTypeOptionsRaw,
    modalShow,
    type,
    city,
    location: locationForEdit,
  } = props

  const runSnackBar = useContext(SnackBarContext)
  const dispatch = useDispatch()

  const title = type === 'add' ? 'Add Location' : 'Edit Location'
  const isEdit = type === 'edit'

  const nextButtonLabel = generateNextButtonLabel(currentStep, type)
  const backButtonLabel = generateBackButtonLabel(currentStep)

  const locationTypeOptions = useMemo(() => {
    // Types is hardcoded in the backend, so we need to filter them out
    return locationTypeOptionsRaw
      .filter(
        option =>
          !['Event', 'Festival', 'Sports / Events'].includes(option.label),
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [locationTypeOptionsRaw])

  const defaultCenter = useMemo(
    () => (props.city ? getDefaultCenter(props.city, locationForEdit) : null),
    [props.city],
  )

  useEffect(() => {
    if (locationForEdit) {
      prepareAndSetLocationSchedule(locationForEdit.schedule)
      const form = {
        ...locationForEdit,
        location: {
          lat: locationForEdit.latitude,
          lng: locationForEdit.longitude,
        },
        needsPermit: locationForEdit.hasPermit,
      }
      setBaseLocationForm(form)
    }
  }, [locationForEdit])

  useEffect(() => {
    setMapCenter(getDefaultCenter(props.city, locationForEdit))
    handleBaseLocationFormChange(
      'location',
      getDefaultCenter(props.city, locationForEdit),
    )
  }, [props.city])

  useEffect(() => {
    setTimeout(() => {
      if (locationForEdit && !baseLocationForm.address) {
        handleMapMarkerDrag(locationForEdit.latitude, locationForEdit.longitude)
      }
      setIsGoogleLoaded(true)
    }, 400)
  }, [])

  const checkScheduleChanges = () => {
    if (!isEdit) {
      // For new locations, check if any schedule data differs from defaults
      const defaultEventSchedule = {
        startDate: null,
        endDate: null,
        breakTimes: [{ id: 1, start: null, end: null }],
      }
      const defaultTimeSlotsSchedule = getDefaultTimeSlotsSchedule()
      const defaultHoursSchedule = getDefaultHoursSchedule()
      const defaultYearlySchedule = [{ id: 1, start: null, end: null }]

      // Check if any schedule differs from defaults
      if (!deepEqual(eventSchedule, defaultEventSchedule)) return true
      if (!deepEqual(timeSlotsSchedule, defaultTimeSlotsSchedule)) return true
      if (!deepEqual(weeklyHoursSchedule, defaultHoursSchedule)) return true
      if (!deepEqual(dailyHoursSchedule, defaultHoursSchedule)) return true
      if (!deepEqual(yearlySchedule, defaultYearlySchedule)) return true
      if (includeYearlySchedule !== false) return true
      if (weeklyScheduleType !== LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS)
        return true

      return false
    } else {
      // For edits, compare current state with original location schedule
      if (!locationForEdit || !locationForEdit.schedule) return false

      const originalSchedule = locationForEdit.schedule

      // Compare weeklyScheduleType
      if (
        originalSchedule.scheduleType !== weeklyScheduleType &&
        !baseLocationForm.isEvent
      )
        return true

      // Compare event schedule
      if (
        baseLocationForm.isEvent &&
        originalSchedule.scheduleType === LOCATION_SCHEDULE_TYPES_ENUM.EVENT
      ) {
        const originalEventSchedule = {
          startDate: originalSchedule.eventStartDate
            ? moment(originalSchedule.eventStartDate).toDate()
            : null,
          endDate: originalSchedule.eventEndDate
            ? moment(originalSchedule.eventEndDate).toDate()
            : null,
          breakTimes: originalSchedule.eventOperationalTimes
            ? originalSchedule.eventOperationalTimes.map(
                (breakTime, index) => ({
                  id: index + 1,
                  start: to12Hour(breakTime.start),
                  end: to12Hour(breakTime.end),
                }),
              )
            : [{ id: 1, start: null, end: null }],
        }
        if (!deepEqual(eventSchedule, originalEventSchedule)) return true
      }

      // Compare time slots schedule
      if (
        originalSchedule.scheduleType ===
        LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS
      ) {
        const originalTimeSlotsSchedule = originalSchedule.slotsScheduleDays
          ? originalSchedule.slotsScheduleDays.map(day => ({
              day: day,
              slots: {
                afternoon: originalSchedule.afternoon,
                lunch: originalSchedule.lunch,
                morning: originalSchedule.morning,
              },
            }))
          : getDefaultTimeSlotsSchedule()
        if (!deepEqual(timeSlotsSchedule, originalTimeSlotsSchedule))
          return true
      }

      // Compare weekly hours schedule
      if (
        originalSchedule.scheduleType ===
        LOCATION_SCHEDULE_TYPES_ENUM.WEEKLY_HOURS
      ) {
        const originalWeeklyHoursSchedule = originalSchedule.daysSchedule
          ? convertScheduleResponseToDailySchedule(
              originalSchedule.daysSchedule,
            )
          : getDefaultHoursSchedule()
        if (!deepEqual(weeklyHoursSchedule, originalWeeklyHoursSchedule))
          return true
      }

      // Compare daily hours schedule
      if (
        originalSchedule.scheduleType ===
        LOCATION_SCHEDULE_TYPES_ENUM.DAILY_HOURS
      ) {
        const originalDailyHoursSchedule = originalSchedule.daysSchedule
          ? convertScheduleResponseToDailySchedule(
              originalSchedule.daysSchedule,
            )
          : getDefaultHoursSchedule()
        if (!deepEqual(dailyHoursSchedule, originalDailyHoursSchedule))
          return true
      }

      // Compare yearly schedule
      const originalYearlySchedule = originalSchedule.yearlySchedule
        ? originalSchedule.yearlySchedule.map((timeRange, index) => ({
            id: index + 1,
            start: timeRange.start.replace('2000-', ''),
            end: timeRange.end.replace('2000-', ''),
          }))
        : [{ id: 1, start: null, end: null }]
      if (!deepEqual(yearlySchedule, originalYearlySchedule)) return true

      // Compare includeYearlySchedule
      const originalIncludeYearlySchedule = !!originalSchedule.yearlySchedule
      if (includeYearlySchedule !== originalIncludeYearlySchedule) return true

      return false
    }
  }

  const checkIfChangesWereMade = () => {
    let form = !isEdit
      ? initialForm
      : {
          name: locationForEdit.name,
          label: locationForEdit.label,
          description: locationForEdit.description,
          needsPermit: locationForEdit.hasPermit,
          typeId: locationForEdit.typeId,
          isEvent: locationForEdit.isEvent,
          isSheltered: locationForEdit.isSheltered,
          eventType: locationForEdit.eventType,
          address: locationForEdit.address,
          location: {
            lat: locationForEdit.latitude,
            lng: locationForEdit.longitude,
          },
        }
    if (isEdit) {
      delete form.address
      delete form.location
    } else {
      delete form.location
    }
    const formChanges = Object.keys(form).reduce((acc, key) => {
      if (baseLocationForm[key] !== form[key]) {
        acc[key] = baseLocationForm[key]
      }
      return acc
    }, {})

    // Check if form data changed
    const hasFormChanges = Object.keys(formChanges).length > 0

    // Check if schedule data changed
    const hasScheduleChanges = checkScheduleChanges()

    return hasFormChanges || hasScheduleChanges
  }

  const prepareAndSetLocationSchedule = schedule => {
    if (schedule.scheduleType === LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS) {
      const data = schedule.slotsScheduleDays.map(day => {
        return {
          day: day,
          slots: {
            afternoon: schedule.afternoon,
            lunch: schedule.lunch,
            morning: schedule.morning,
          },
        }
      })
      setTimeSlotsSchedule(data)
    }

    if (
      [LOCATION_SCHEDULE_TYPES_ENUM.WEEKLY_HOURS].includes(
        schedule.scheduleType,
      )
    ) {
      setWeeklyHoursSchedule(
        convertScheduleResponseToDailySchedule(schedule.daysSchedule),
      )
      setWeeklyScheduleType(schedule.scheduleType)
    }

    if (
      [LOCATION_SCHEDULE_TYPES_ENUM.DAILY_HOURS].includes(schedule.scheduleType)
    ) {
      setDailyHoursSchedule(
        convertScheduleResponseToDailySchedule(schedule.daysSchedule),
      )
      setWeeklyScheduleType(schedule.scheduleType)
    }

    if ([LOCATION_SCHEDULE_TYPES_ENUM.EVENT].includes(schedule.scheduleType)) {
      setEventSchedule({
        startDate: moment(schedule.eventStartDate).toDate(),
        endDate: moment(schedule.eventEndDate).toDate(),
        breakTimes: schedule.eventOperationalTimes.map(
          (operationalTime, index) => ({
            id: index + 1,
            start: to12Hour(operationalTime.start),
            end: to12Hour(operationalTime.end),
          }),
        ),
      })
    }
    if (schedule.yearlySchedule) {
      setYearlySchedule(
        schedule.yearlySchedule.map((timeRange, index) => ({
          id: index + 1,
          start: timeRange.start.replace('2000-', ''),
          end: timeRange.end.replace('2000-', ''),
        })),
      )
      setIncludeYearlySchedule(true)
    }
  }

  const prepareSchedulePayload = () => {
    const yearlySchedulePayload =
      includeYearlySchedule && !baseLocationForm.isEvent
        ? {
            yearlySchedule: yearlySchedule.map(({ start, end }) => ({
              start: `2000-${start}`,
              end: `2000-${end}`,
            })),
          }
        : {
            yearlySchedule: null,
          }
    const id = locationForEdit ? locationForEdit.schedule.id : null

    if (baseLocationForm.isEvent) {
      return {
        ...yearlySchedulePayload,
        id: id,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.EVENT,
        eventStartDate: moment(eventSchedule.startDate).format('YYYY-MM-DD'),
        eventEndDate: moment(eventSchedule.endDate).format('YYYY-MM-DD'),
        eventOperationalTimes: eventSchedule.breakTimes.map(
          operationalTime => ({
            start: to24Hour(operationalTime.start),
            end: to24Hour(operationalTime.end),
          }),
        ),
      }
    }

    if (weeklyScheduleType === LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS) {
      return {
        ...yearlySchedulePayload,
        id: id,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS,
        slotsScheduleDays: timeSlotsSchedule.map(day => day.day),
        morning: timeSlotsSchedule[0].slots.morning,
        lunch: timeSlotsSchedule[0].slots.lunch,
        afternoon: timeSlotsSchedule[0].slots.afternoon,
      }
    }

    if (weeklyScheduleType === LOCATION_SCHEDULE_TYPES_ENUM.WEEKLY_HOURS) {
      return {
        ...yearlySchedulePayload,
        id: id,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.WEEKLY_HOURS,
        daysSchedule: prepareDailySchedulePayload(weeklyHoursSchedule),
      }
    }

    if (weeklyScheduleType === LOCATION_SCHEDULE_TYPES_ENUM.DAILY_HOURS) {
      return {
        ...yearlySchedulePayload,
        id: id,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.DAILY_HOURS,
        daysSchedule: prepareDailySchedulePayload(dailyHoursSchedule),
      }
    }
  }

  const handleBaseLocationFormChange = (key, value) => {
    setBaseLocationForm(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleFormSubmit = () => {
    const { city, handleSuccess } = props
    const payload = {
      city_id: city.id,
      name: baseLocationForm.name,
      label: baseLocationForm.label,
      is_event: baseLocationForm.isEvent,
      type_id: baseLocationForm.typeId,
      latitude: baseLocationForm.location.lat,
      longitude: baseLocationForm.location.lng,
      moments_morning: false,
      moments_lunch: false,
      moments_afternoon: false,
      require_permit: baseLocationForm.needsPermit,
      is_sheltered: baseLocationForm.isSheltered,
      status: 0,
      event_type: baseLocationForm.isEvent ? baseLocationForm.eventType : null,
      description: baseLocationForm.description,
      address: baseLocationForm.address,
      schedule: prepareSchedulePayload(),
    }
    const callbacks = {
      onSuccess: () => {
        runSnackBar({
          type: 'success',
          msg: `"${baseLocationForm.name}" location created successfully!`,
        })
        handleSuccess()
      },
      onError: () => {
        runSnackBar({
          type: 'error',
          msg: `Something went wrong!`,
        })
        setIsSubmitting(false)
      },
    }

    dispatch(createLocation(callbacks, city.id, payload))
  }

  const handleFormEditSubmit = () => {
    const { handleSuccess, location } = props
    const payload = {
      name: baseLocationForm.name.replace('(Archived)', ''),
      label: baseLocationForm.label,
      is_event: baseLocationForm.isEvent,
      type_id: baseLocationForm.typeId,
      is_sheltered: baseLocationForm.isSheltered,
      event_type: baseLocationForm.isEvent ? baseLocationForm.eventType : null,
      description: baseLocationForm.description,
      require_permit: baseLocationForm.needsPermit,
      address: baseLocationForm.address,
      schedule: prepareSchedulePayload(),
    }
    const callbacks = {
      onSuccess: () => {
        runSnackBar({
          type: 'success',
          msg: `Changes Saved!`,
        })
        handleSuccess()
      },
      onError: () => {
        runSnackBar({
          type: 'error',
          msg: `Something went wrong!`,
        })
        setIsSubmitting(false)
      },
    }
    dispatch(updateLocationById(callbacks, location.id, payload))
  }

  const handleLocatonArchive = () => {
    const { handleSuccess, location } = props
    const callbacks = {
      onSuccess: () => {
        runSnackBar({
          type: 'success',
          msg: `${location.name} archived!`,
        })
        handleSuccess()
      },
    }
    dispatch(archiveLocation(callbacks, location.id))
  }

  const handleLocatonUnarchive = () => {
    const { handleSuccess, location } = props
    const payload = {
      status: 1,
    }
    const callbacks = {
      onSuccess: () => {
        runSnackBar({
          type: 'success',
          msg: `${baseLocationForm.name} unarchived!`,
        })
        handleSuccess()
      },
    }
    dispatch(updateLocationById(callbacks, location.id, payload))
  }

  const onCancel = () => {
    const changesWereMade = checkIfChangesWereMade()
    if (!changesWereMade) {
      handleCancel()
      return
    }
    if (changesWereMade && !closeWarnShown) {
      setCloseWarnShown(true)
      document.getElementById('modal-content').scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
      setTimeout(() => {
        setCloseWarnShown(false)
      }, 4000)
    }
  }

  const handleNextButton = () => {
    if (currentStep === MODAL_STEPS.BASE_INFORMATION) {
      return setCurrentStep(MODAL_STEPS.SCHEDULE)
    }

    if (currentStep === MODAL_STEPS.SCHEDULE && baseLocationForm.isEvent) {
      const hasErrors = validateEventSchedule(
        eventSchedule,
        handleValidationError,
      )
      if (hasErrors) {
        return
      }
    } else if (
      currentStep === MODAL_STEPS.SCHEDULE &&
      !baseLocationForm.isEvent
    ) {
      //Yearly schedule validations
      const yearlyScheduleHasErrors =
        includeYearlySchedule &&
        validateYearlySchedule(yearlySchedule, handleValidationError)

      const weeklyScheduleHasErrors =
        weeklyScheduleType === LOCATION_SCHEDULE_TYPES_ENUM.WEEKLY_HOURS
          ? validateWeeklySchedule(weeklyHoursSchedule, handleValidationError)
          : false
      const dailyScheduleHasErrors =
        weeklyScheduleType === LOCATION_SCHEDULE_TYPES_ENUM.DAILY_HOURS
          ? validateDaylySchedule(dailyHoursSchedule, handleValidationError)
          : false

      if (
        yearlyScheduleHasErrors ||
        weeklyScheduleHasErrors ||
        dailyScheduleHasErrors
      ) {
        return
      }
    }

    const haveGeneralErrors =
      validationErrors['days-selector'] || validationErrors['time-slots']

    if (haveGeneralErrors) {
      return
    }
    try {
      setIsSubmitting(true)
      type === 'add' ? handleFormSubmit() : handleFormEditSubmit()
    } catch (error) {
      setIsSubmitting(false)
      runSnackBar({
        type: 'error',
        msg: `Something went wrong!`,
      })
      console.error(error)
    } finally {
      //setIsSubmitting(false)
    }
  }

  const handleBackButton = () => {
    if (currentStep === MODAL_STEPS.BASE_INFORMATION) {
      onCancel()
    } else {
      setCurrentStep(MODAL_STEPS.BASE_INFORMATION)
    }
  }

  const handleMapMarkerDrag = (lat, lng) => {
    if (!window.google || !window.google.maps) return
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const newLocation = {
          lat,
          lng,
        }

        const formattedAddress = formatAddress(results[0].address_components)

        handleBaseLocationFormChange('location', newLocation)
        setMapCenter(newLocation)
        handleBaseLocationFormChange('address', formattedAddress)
      } else {
        handleBaseLocationFormChange('location', { lat, lng })
        handleBaseLocationFormChange('address', '')
      }
    })
  }

  const handlePlaceChanged = place => {
    const lat =
      typeof place.geometry.location.lat === 'function'
        ? place.geometry.location.lat()
        : place.geometry.location.lat
    const lng =
      typeof place.geometry.location.lng === 'function'
        ? place.geometry.location.lng()
        : place.geometry.location.lng

    const newLocation = {
      lat,
      lng,
    }

    handleBaseLocationFormChange('location', newLocation)
    setMapCenter(newLocation)
    handleBaseLocationFormChange('address', place.formattedAddress)
  }

  const checkNextButtonAvailability = () => {
    if (isSubmitting) {
      return false
    }
    if (currentStep === MODAL_STEPS.BASE_INFORMATION) {
      const requredFields = [
        'name',
        'label',
        'address',
        'typeId',
        ...(baseLocationForm.isEvent ? ['eventType', 'description'] : []),
      ]
      return requredFields.every(
        field =>
          baseLocationForm[field] !== '' &&
          baseLocationForm[field] !== null &&
          baseLocationForm[field] !== undefined,
      )
    } else {
      return true
    }
  }

  const handleEventScheduleChange = (key, value) => {
    setEventSchedule(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleValidationError = (key, error) => {
    return setValidationErrors(prev => ({
      ...prev,
      [key]: error,
    }))
  }

  const isNextButtonDisabled = !checkNextButtonAvailability()

  return (
    <Modal show={modalShow} id="modal-content">
      <div style={styles.container}>
        <button
          onClick={onCancel}
          style={styles.closeButton}
          onMouseEnter={e => {
            e.target.style.backgroundColor = '#f5f5f5'
          }}
          onMouseLeave={e => {
            e.target.style.backgroundColor = 'transparent'
          }}
          title="Close"
        >
          ×
        </button>
        {closeWarnShown && <UnsavedChangesWarn onCancel={handleCancel} />}
        <div style={styles.title}>{title}</div>

        <div style={styles.subtitle}>
          {isEdit ? null : 'Create a new location for operations'}
        </div>
        <StepsIndicator currentStep={currentStep} />
        {currentStep === MODAL_STEPS.SCHEDULE && !baseLocationForm.isEvent && (
          <div style={styles.subtitle}>
            Define when this location is operational for AI Roster scheduling.
            You can set both weekly and yearly schedules.
          </div>
        )}
        <BaseInformation
          baseLocationForm={baseLocationForm}
          handleLocatonArchive={handleLocatonArchive}
          handleLocatonUnarchive={handleLocatonUnarchive}
          handleBaseLocationFormChange={handleBaseLocationFormChange}
          locationTypeOptions={locationTypeOptions}
          handlePlaceChanged={handlePlaceChanged}
          isGoogleLoaded={isGoogleLoaded}
          city={city}
          mapCenter={mapCenter}
          defaultCenter={defaultCenter}
          handleMapMarkerDrag={handleMapMarkerDrag}
          visible={currentStep === MODAL_STEPS.BASE_INFORMATION}
          isEdit={isEdit}
        />
        <EventSchedule
          eventSchedule={eventSchedule}
          handleEventScheduleChange={handleEventScheduleChange}
          city={city}
          visible={
            currentStep === MODAL_STEPS.SCHEDULE && baseLocationForm.isEvent
          }
          handleValidationError={handleValidationError}
          validationErrors={validationErrors}
        />
        <WeeklyScheduling
          weeklyScheduleType={weeklyScheduleType}
          timeSlotsSchedule={timeSlotsSchedule}
          setTimeSlotsSchedule={setTimeSlotsSchedule}
          weeklyHoursSchedule={weeklyHoursSchedule}
          setWeeklyHoursSchedule={setWeeklyHoursSchedule}
          dailyHoursSchedule={dailyHoursSchedule}
          setDailyHoursSchedule={setDailyHoursSchedule}
          setWeeklyScheduleType={setWeeklyScheduleType}
          visible={
            currentStep === MODAL_STEPS.SCHEDULE && !baseLocationForm.isEvent
          }
          handleValidationError={handleValidationError}
          validationErrors={validationErrors}
        />
        <YearlyScheduling
          yearlySchedule={yearlySchedule}
          setYearlySchedule={setYearlySchedule}
          city={city}
          visible={
            currentStep === MODAL_STEPS.SCHEDULE && !baseLocationForm.isEvent
          }
          includeYearlySchedule={includeYearlySchedule}
          setIncludeYearlySchedule={setIncludeYearlySchedule}
          handleValidationError={handleValidationError}
          validationErrors={validationErrors}
        />
        <div style={styles.buttonContainer}>
          {isSubmitting ? (
            <LinearProgressBar isLoading newDesign styles={{ width: '100%' }} />
          ) : (
            <div style={{ display: 'contents' }}>
              <SubmitButton
                variant="outlined"
                size="small"
                value={backButtonLabel}
                onClick={handleBackButton}
                style={styles.button}
                disabled={isSubmitting}
              />
              <SubmitButton
                disabled={isNextButtonDisabled}
                value={nextButtonLabel}
                size="small"
                onClick={handleNextButton}
                style={styles.button}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default H2HLocationModal
